import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import ItemFormModal from './ItemFormModal';
import ItemDetailModal from './ItemDetailModal';
import useScrollRestoration from '../../hooks/useScrollRestoration';
import './InventoryPage.css';

// ─── Icons ────────────────────────────────────────────────────────────────────

const IconSearch = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
  </svg>
);
const IconChevron = ({ open }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    style={{ transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s', flexShrink: 0 }}>
    <polyline points="9 18 15 12 9 6"/>
  </svg>
);
const IconWarning = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
    <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
  </svg>
);
const IconPack = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
    <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
    <line x1="12" y1="22.08" x2="12" y2="12"/>
  </svg>
);
const IconPlus = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
);
const IconTrash = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="3 6 5 6 21 6"/>
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
    <path d="M10 11v6M14 11v6M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
  </svg>
);
const IconPencil = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);
const IconX = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);
const IconCheck = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);
const IconSettings = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="3"/>
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
  </svg>
);

// ─── Helpers ──────────────────────────────────────────────────────────────────

function labelSortKey(label) {
  if (!label) return { n: Infinity, s: '' };
  const m = label.match(/^#(\d+)$/);
  return m ? { n: parseInt(m[1], 10), s: '' } : { n: Infinity, s: label };
}

function groupByName(items) {
  const groups = {};
  for (const item of items) {
    if (!groups[item.name]) groups[item.name] = [];
    groups[item.name].push(item);
  }
  for (const name of Object.keys(groups)) {
    groups[name].sort((a, b) => {
      const ka = labelSortKey(a.unit_label);
      const kb = labelSortKey(b.unit_label);
      if (ka.n !== kb.n) return ka.n - kb.n;
      return ka.s.localeCompare(kb.s, 'es');
    });
  }
  return groups;
}

function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
}

function StatusBadge({ item }) {
  if (item.has_open_incident) return <span className="badge badge-yellow">Incidencia</span>;
  if (!item.is_operational) return <span className="badge badge-red">No operativo</span>;
  return <span className="badge badge-green">Operativo</span>;
}

// ─── Incident Report Modal ────────────────────────────────────────────────────

const STATUS_LABELS = {
  abierta: 'Abierta',
  en_reparacion: 'En reparación',
  resuelta: 'Resuelta',
};
const STATUS_BADGE = {
  abierta: 'badge-red',
  en_reparacion: 'badge-yellow',
  resuelta: 'badge-green',
};

function IncidentModal({ target, onClose, onSaved }) {
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ description: '', can_operate: false });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [statusSaving, setStatusSaving] = useState(null);

  const endpoint = target.type === 'item' ? 'items' : 'packs';

  const fetchIncidents = useCallback(async () => {
    try {
      const res = await api.get(`/inventory/${endpoint}/${target.id}`);
      setIncidents(res.data.incidents || []);
    } catch {
      // ignore, incidents stay empty
    } finally {
      setLoading(false);
    }
  }, [endpoint, target.id]);

  useEffect(() => { fetchIncidents(); }, [fetchIncidents]);

  const handleStatusChange = async (incidentId, newStatus) => {
    setStatusSaving(incidentId);
    try {
      await api.put(`/inventory/incidents/${incidentId}`, { status: newStatus });
      await fetchIncidents();
      onSaved();
    } catch {
      // silent — status reverts on re-fetch
    } finally {
      setStatusSaving(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await api.post('/inventory/incidents', {
        [target.type === 'item' ? 'item_id' : 'pack_id']: target.id,
        description: form.description,
        can_operate: form.can_operate,
      });
      setForm({ description: '', can_operate: false });
      await fetchIncidents();
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al reportar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <div className="modal-title">Incidencias</div>
        <div className="text-dim text-sm" style={{ marginBottom: 16 }}>{target.name}</div>

        {loading ? (
          <div className="text-dim text-sm" style={{ marginBottom: 16 }}>Cargando...</div>
        ) : incidents.length === 0 ? (
          <div className="text-dim text-sm" style={{ marginBottom: 16 }}>Sin incidencias registradas.</div>
        ) : (
          <div className="inv-incidents-list" style={{ marginBottom: 20 }}>
            {incidents.map(inc => (
              <div key={inc.id} className="inv-incident-card">
                <div className="inv-incident-header">
                  <span className={`badge ${STATUS_BADGE[inc.status]}`}>{STATUS_LABELS[inc.status]}</span>
                  <span className="text-dim text-sm">{new Date(inc.reported_at).toLocaleDateString('es-AR')}</span>
                  <span className="text-dim text-sm">{inc.reported_by_name}</span>
                </div>
                <div style={{ fontSize: 13, marginBottom: 8 }}>{inc.description}</div>
                {inc.status === 'resuelta' && inc.resolved_by_name && (
                  <div className="text-dim text-sm" style={{ marginBottom: 8 }}>
                    Resuelta por {inc.resolved_by_name}
                  </div>
                )}
                <select
                  className="input"
                  style={{ fontSize: 13, padding: '4px 8px' }}
                  value={inc.status}
                  disabled={statusSaving === inc.id}
                  onChange={e => handleStatusChange(inc.id, e.target.value)}
                >
                  <option value="abierta">Abierta</option>
                  <option value="en_reparacion">En reparación</option>
                  <option value="resuelta">Resuelta</option>
                </select>
              </div>
            ))}
          </div>
        )}

        <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16 }}>
          <div className="form-label" style={{ marginBottom: 10 }}>Nueva incidencia</div>
          <form onSubmit={handleSubmit}>
            {error && <div className="inv-error">{error}</div>}
            <div className="form-group">
              <textarea
                className="input"
                rows={3}
                placeholder="¿Qué pasó con el equipo?"
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                required
                style={{ resize: 'vertical' }}
              />
            </div>
            <label className="inv-checkbox-label" style={{ marginBottom: 16 }}>
              <input
                type="checkbox"
                checked={form.can_operate}
                onChange={e => setForm(f => ({ ...f, can_operate: e.target.checked }))}
              />
              El equipo puede seguir operando
            </label>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" className="btn btn-ghost" onClick={onClose}>Cerrar</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Guardando...' : 'Reportar'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// ─── Pack Modal (create / edit + manage items) ────────────────────────────────

function PackModal({ pack, allItems, onClose, onSaved }) {
  const isEdit = !!pack;
  const [name, setName] = useState(pack?.name || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [packItems, setPackItems] = useState(pack?.items || []);
  const [addSearch, setAddSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);

  const availableToAdd = allItems.filter(
    i => !i.pack_id && !packItems.find(pi => pi.id === i.id)
  );
  const filteredAvailable = addSearch
    ? availableToAdd.filter(i => i.name.toLowerCase().includes(addSearch.toLowerCase()))
    : availableToAdd;

  const handleSave = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    setError('');
    try {
      if (isEdit) {
        await api.put(`/inventory/packs/${pack.id}`, {
          name: name.trim(),
          quantity: pack.quantity ?? 0,
          notes: pack.notes || null,
        });
      } else {
        await api.post('/inventory/packs', { name: name.trim() });
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveItem = async (item) => {
    try {
      await api.put(`/inventory/items/${item.id}`, {
        name: item.name, category: item.category,
        subcategory: item.subcategory, quantity: item.quantity,
        notes: item.notes || null, pack_id: null,
      });
      setPackItems(prev => prev.filter(i => i.id !== item.id));
      onSaved();
    } catch (err) {
      alert(err.response?.data?.error || 'Error al quitar ítem');
    }
  };

  const handleAddItem = async (item) => {
    try {
      await api.put(`/inventory/items/${item.id}`, {
        name: item.name, category: item.category,
        subcategory: item.subcategory, quantity: item.quantity,
        notes: item.notes || null, pack_id: pack.id,
      });
      setPackItems(prev => [...prev, item]);
      setAddSearch('');
      onSaved();
    } catch (err) {
      alert(err.response?.data?.error || 'Error al agregar ítem');
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
        <div className="modal-title">{isEdit ? 'Editar pack' : 'Nuevo pack'}</div>
        <form onSubmit={handleSave}>
          {error && <div className="inv-error">{error}</div>}
          <div className="form-group">
            <label className="form-label">Nombre del pack</label>
            <input
              className="input"
              value={name}
              onChange={e => setName(e.target.value)}
              required
              autoFocus
            />
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginBottom: isEdit ? 20 : 0 }}>
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancelar</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Guardando...' : isEdit ? 'Guardar nombre' : 'Crear pack'}
            </button>
          </div>
        </form>

        {isEdit && (
          <>
            <div className="divider" />
            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10 }}>
              Ítems en el pack ({packItems.length})
            </div>
            {packItems.length === 0 ? (
              <p className="text-dim text-sm" style={{ marginBottom: 12 }}>Sin ítems asignados</p>
            ) : (
              <div className="inv-pack-items-list">
                {packItems.map(item => (
                  <div key={item.id} className="inv-pack-item-row">
                    <div style={{ minWidth: 0 }}>
                      <div className="text-sm" style={{ fontWeight: 500 }}>{item.name}</div>
                      <div className="text-xs text-dim">{item.subcategory}</div>
                    </div>
                    <button
                      className="btn btn-icon btn-ghost"
                      style={{ color: 'var(--red)', flexShrink: 0 }}
                      onClick={() => handleRemoveItem(item)}
                      title="Quitar del pack"
                    >
                      <IconX />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {showAdd ? (
              <div style={{ marginTop: 8 }}>
                <input
                  className="input"
                  style={{ marginBottom: 8 }}
                  placeholder="Buscar ítem para agregar..."
                  value={addSearch}
                  onChange={e => setAddSearch(e.target.value)}
                  autoFocus
                />
                {filteredAvailable.length === 0 ? (
                  <p className="text-dim text-sm">No hay ítems disponibles sin pack</p>
                ) : (
                  <div className="inv-pack-items-list inv-pack-items-list--scroll">
                    {filteredAvailable.map(item => (
                      <div
                        key={item.id}
                        className="inv-pack-item-row inv-pack-item-add"
                        onClick={() => handleAddItem(item)}
                      >
                        <div style={{ minWidth: 0 }}>
                          <div className="text-sm" style={{ fontWeight: 500 }}>{item.name}</div>
                          <div className="text-xs text-dim">{item.category} · {item.subcategory}</div>
                        </div>
                        <IconPlus />
                      </div>
                    ))}
                  </div>
                )}
                <button
                  className="btn btn-ghost btn-sm"
                  style={{ marginTop: 8 }}
                  onClick={() => { setShowAdd(false); setAddSearch(''); }}
                >
                  Cerrar
                </button>
              </div>
            ) : (
              availableToAdd.length > 0 && (
                <button
                  className="btn btn-ghost btn-sm"
                  style={{ display: 'flex', alignItems: 'center', gap: 4 }}
                  onClick={() => setShowAdd(true)}
                >
                  <IconPlus /> Agregar ítem
                </button>
              )
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ─── Delete Confirm Modal ─────────────────────────────────────────────────────

function DeleteModal({ name, onClose, onConfirm }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <h3 className="modal-title">¿Eliminar?</h3>
        <p style={{ color: 'var(--text-dim)', fontSize: 14, marginBottom: 8 }}>
          Vas a eliminar permanentemente:
        </p>
        <p style={{ fontWeight: 600, marginBottom: 24 }}>{name}</p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn btn-ghost" onClick={onClose}>Cancelar</button>
          <button className="btn btn-danger" onClick={onConfirm}>Eliminar</button>
        </div>
      </div>
    </div>
  );
}

// ─── Consulta Tab ─────────────────────────────────────────────────────────────

function ConsultaTab({ items, packs, canReport, isAdmin, categories = [], onReportItem, onReportPack, onViewItem, onUpdateItem, onAdd, onDeleteItem, onEditPack, onDeletePack, onEditGroup, onManageCategories }) {
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('Todas');
  const [onlyIncidents, setOnlyIncidents] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [expandedPacks, setExpandedPacks] = useState(new Set());
  const [editingLabelId, setEditingLabelId] = useState(null);
  const [editingLabelValue, setEditingLabelValue] = useState('');
  const [savingLabelId, setSavingLabelId] = useState(null);

  const handleSaveLabel = async (item) => {
    setSavingLabelId(item.id);
    try {
      await api.put(`/inventory/items/${item.id}`, {
        name: item.name, category: item.category, subcategory: item.subcategory,
        quantity: item.quantity, notes: item.notes || null,
        pack_id: item.pack_id || null, unit_label: editingLabelValue.trim() || null,
      });
      onUpdateItem({ ...item, unit_label: editingLabelValue.trim() || null });
      setEditingLabelId(null);
    } catch (e) {
      alert(e.response?.data?.error || 'Error al guardar');
    } finally {
      setSavingLabelId(null);
    }
  };

  const toggleGroup = name => setExpandedGroups(prev => {
    const next = new Set(prev); next.has(name) ? next.delete(name) : next.add(name); return next;
  });
  const togglePack = id => setExpandedPacks(prev => {
    const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next;
  });

  const categoryBlocks = useMemo(() => {
    const lc = search.toLowerCase();
    const blocks = [];

    const buildBlock = (cat, catItems, catPacks) => {
      if (!catItems.length && !catPacks.length) return null;
      const subMap = {};
      catItems.forEach(i => {
        const k = i.subcategory || 'General';
        if (!subMap[k]) subMap[k] = { items: [], packs: [] };
        subMap[k].items.push(i);
      });
      catPacks.forEach(p => {
        const k = p.subcategory || 'General';
        if (!subMap[k]) subMap[k] = { items: [], packs: [] };
        subMap[k].packs.push(p);
      });
      const subKeys = Object.keys(subMap).sort((a, b) => {
        if (a === 'General') return 1;
        if (b === 'General') return -1;
        return a.localeCompare(b, 'es');
      });
      return {
        cat,
        sections: subKeys.map(sub => ({
          sub,
          itemGroups: groupByName(subMap[sub].items),
          packs: subMap[sub].packs.sort((a, b) => a.name.localeCompare(b.name, 'es')),
        })),
      };
    };

    const catOrder = categories.length ? categories.map(c => c.name) : ['Audio', 'Iluminación', 'Video', 'IT / Redes', 'Estructura'];
    for (const cat of catOrder) {
      if (activeCategory !== 'Todas' && cat !== activeCategory) continue;
      const catItems = items.filter(i =>
        i.category === cat &&
        (!onlyIncidents || i.has_open_incident) &&
        (!lc || i.name.toLowerCase().includes(lc))
      );
      const catPacks = packs.filter(p =>
        p.category === cat &&
        (!onlyIncidents || p.has_open_incident) &&
        (!lc || p.name.toLowerCase().includes(lc))
      );
      const block = buildBlock(cat, catItems, catPacks);
      if (block) blocks.push(block);
    }

    // Fallback block for items/packs without category
    if (activeCategory === 'Todas' || activeCategory === 'Sin categoría') {
      const uncatItems = items.filter(i =>
        !i.category &&
        (!onlyIncidents || i.has_open_incident) &&
        (!lc || i.name.toLowerCase().includes(lc))
      );
      const uncatPacks = packs.filter(p =>
        !p.category &&
        (!onlyIncidents || p.has_open_incident) &&
        (!lc || p.name.toLowerCase().includes(lc))
      );
      const block = buildBlock('Sin categoría', uncatItems, uncatPacks);
      if (block) blocks.push(block);
    }

    return blocks;
  }, [items, packs, search, onlyIncidents, activeCategory]);

  const availableCategories = useMemo(() => {
    const inData = new Set([
      ...items.map(i => i.category).filter(Boolean),
      ...packs.map(p => p.category).filter(Boolean),
    ]);
    const order = categories.length ? categories.map(c => c.name) : ['Audio', 'Iluminación', 'Video', 'IT / Redes', 'Estructura'];
    return order.filter(c => inData.has(c));
  }, [items, packs, categories]);

  return (
    <div>
      <div className="inv-toolbar">
        <div className="inv-search-wrapper">
          <span className="inv-search-icon"><IconSearch /></span>
          <input
            className="input"
            placeholder="Buscar equipo..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <button
          className={`btn btn-ghost btn-sm inv-incidents-toggle${onlyIncidents ? ' active-tab' : ''}`}
          onClick={() => setOnlyIncidents(v => !v)}
        >
          <IconWarning /> Con incidencias
        </button>
        {isAdmin && (
          <button className="btn btn-primary btn-sm inv-add-btn" onClick={onAdd}>
            <IconPlus /> Nuevo
          </button>
        )}
      </div>

      <div className="inv-category-tabs">
        {['Todas', ...availableCategories].map(cat => (
          <button
            key={cat}
            className={`btn btn-ghost btn-sm${activeCategory === cat ? ' active-tab' : ''}`}
            onClick={() => setActiveCategory(activeCategory === cat && cat !== 'Todas' ? 'Todas' : cat)}
          >
            {cat}
          </button>
        ))}
        {isAdmin && (
          <button
            className="btn btn-icon btn-ghost btn-sm"
            title="Gestionar categorías"
            style={{ marginLeft: 'auto', flexShrink: 0 }}
            onClick={onManageCategories}
          ><IconSettings /></button>
        )}
      </div>

      {categoryBlocks.length === 0 ? (
        <div className="inv-empty">
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
          </svg>
          <p>Sin resultados</p>
        </div>
      ) : (
        <div>
          {categoryBlocks.map(({ cat, sections }) => (
            <div key={cat} className="inv-category-block" id={`cat-${cat.replace(/[\s/]+/g, '-')}`}>
              <div className="inv-category-header">{cat}</div>
              {sections.map(({ sub, itemGroups, packs: sectionPacks }) => (
                <div key={sub} className="inv-subcat-section">
                  {sections.length > 1 && (
                    <div className="inv-subcat-header">{sub}</div>
                  )}
                  <div className="inv-list">
                    {sectionPacks.map(pack => (
                      <div key={`pack-${pack.id}`} className="inv-group-block">
                        <div
                          className={`inv-group-header${expandedPacks.has(pack.id) ? ' expanded' : ''}`}
                          onClick={() => togglePack(pack.id)}
                        >
                          <span className="inv-group-icon"><IconPack /></span>
                          <span className="inv-group-name">{pack.name}</span>
                          <span className="inv-group-count">{pack.items?.length || 0} ítems</span>
                          <span className="badge" style={{ background: 'var(--surface-raised)', color: 'var(--text-dim)', fontSize: 11, fontWeight: 500 }}>Pack</span>
                          {pack.has_open_incident && <span className="badge badge-yellow">Incidencia</span>}
                          {!pack.is_operational && !pack.has_open_incident && (
                            <span className="badge badge-red">No operativo</span>
                          )}
                          <div className="inv-unit-actions" onClick={e => e.stopPropagation()}>
                            {canReport && (
                              <button className="btn btn-icon btn-ghost" title="Reportar incidencia" onClick={() => onReportPack(pack)}><IconWarning /></button>
                            )}
                            {isAdmin && (
                              <>
                                <button className="btn btn-icon btn-ghost" onClick={() => onEditPack(pack)} title="Editar pack"><IconPencil /></button>
                                <button className="btn btn-icon btn-ghost" style={{ color: 'var(--red)' }} onClick={() => onDeletePack(pack)} title="Eliminar pack"><IconTrash /></button>
                              </>
                            )}
                          </div>
                          <IconChevron open={expandedPacks.has(pack.id)} />
                        </div>
                        {expandedPacks.has(pack.id) && (
                          <div className="inv-group-body">
                            {!pack.items?.length ? (
                              <div className="inv-unit-row inv-unit-empty">Sin ítems en este pack</div>
                            ) : pack.items.map(item => (
                              <div key={item.id} className="inv-unit-row" onClick={() => onViewItem(item)}>
                                <div className="inv-unit-info">
                                  <span className="inv-unit-name">{item.name}</span>
                                  <span className="text-xs text-dim">{item.unit_label || item.subcategory}</span>
                                </div>
                                <div className="inv-unit-actions">
                                  <StatusBadge item={item} />
                                  {canReport && (
                                    <button
                                      className="btn btn-icon btn-ghost"
                                      title="Reportar incidencia"
                                      onClick={e => { e.stopPropagation(); onReportItem(item); }}
                                    >
                                      <IconWarning />
                                    </button>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}

                    {Object.entries(itemGroups)
                      .sort(([a], [b]) => a.localeCompare(b, 'es'))
                      .map(([name, units]) => (
                        <div key={`group-${name}`} className="inv-group-block">
                          <div
                            className={`inv-group-header${expandedGroups.has(name) ? ' expanded' : ''}`}
                            onClick={() => toggleGroup(name)}
                          >
                            <span className="inv-group-name">{name}</span>
                            {units.length > 1 && (
                              <span className="inv-group-count">×{units.length}</span>
                            )}
                            {units.some(u => u.has_open_incident) && (
                              <span className="badge badge-yellow">Incidencia</span>
                            )}
                            {!units.some(u => u.has_open_incident) && units.some(u => !u.is_operational) && (
                              <span className="badge badge-red">No operativo</span>
                            )}
                            {isAdmin && (
                              <div className="inv-unit-actions" onClick={e => e.stopPropagation()}>
                                <button
                                  className="btn btn-icon btn-ghost"
                                  title="Editar grupo"
                                  onClick={() => onEditGroup(units)}
                                ><IconPencil /></button>
                              </div>
                            )}
                            <IconChevron open={expandedGroups.has(name)} />
                          </div>
                          {expandedGroups.has(name) && (
                            <div className="inv-group-body">
                              {units.map(item => (
                                <div
                                  key={item.id}
                                  className="inv-unit-row"
                                  onClick={() => editingLabelId !== item.id && onViewItem(item)}
                                >
                                  <div className="inv-unit-info">
                                    {editingLabelId === item.id ? (
                                      <div
                                        style={{ display: 'flex', alignItems: 'center', gap: 4 }}
                                        onClick={e => e.stopPropagation()}
                                      >
                                        <input
                                          className="input"
                                          style={{ padding: '2px 8px', height: 30, fontSize: 13, width: 140 }}
                                          value={editingLabelValue}
                                          onChange={e => setEditingLabelValue(e.target.value)}
                                          onKeyDown={e => {
                                            if (e.key === 'Enter') handleSaveLabel(item);
                                            if (e.key === 'Escape') setEditingLabelId(null);
                                          }}
                                          autoFocus
                                        />
                                        <button
                                          className="btn btn-icon btn-ghost"
                                          onClick={() => handleSaveLabel(item)}
                                          disabled={savingLabelId === item.id}
                                          title="Guardar"
                                        ><IconCheck /></button>
                                        <button
                                          className="btn btn-icon btn-ghost"
                                          onClick={() => setEditingLabelId(null)}
                                          title="Cancelar"
                                        ><IconX /></button>
                                      </div>
                                    ) : (
                                      <>
                                        <span style={{ fontWeight: 500, fontSize: 13 }}>{item.unit_label || '—'}</span>
                                        <span className="text-xs text-dim">{fmtDate(item.created_at)}</span>
                                      </>
                                    )}
                                  </div>
                                  <div className="inv-unit-actions" onClick={e => e.stopPropagation()}>
                                    {item.pack_id && item.pack_name && (
                                      <span className="badge" style={{ background: 'var(--blue-dim, #1e3a5f)', color: 'var(--blue, #60a5fa)', fontSize: 11, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                        Pack: {item.pack_name}
                                      </span>
                                    )}
                                    <StatusBadge item={item} />
                                    {canReport && (
                                      <button
                                        className="btn btn-icon btn-ghost"
                                        title="Reportar incidencia"
                                        onClick={() => onReportItem(item)}
                                      ><IconWarning /></button>
                                    )}
                                    {isAdmin && editingLabelId !== item.id && (
                                      <>
                                        <button
                                          className="btn btn-icon btn-ghost"
                                          title="Editar etiqueta"
                                          onClick={() => { setEditingLabelId(item.id); setEditingLabelValue(item.unit_label || ''); }}
                                        ><IconPencil /></button>
                                        <button
                                          className="btn btn-icon btn-ghost"
                                          style={{ color: 'var(--red)' }}
                                          title="Eliminar"
                                          onClick={() => onDeleteItem(item)}
                                        ><IconTrash /></button>
                                      </>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Categories Modal ─────────────────────────────────────────────────────────

function CategoriesModal({ categories, onClose, onSaved }) {
  const [expandedCats, setExpandedCats] = useState(() => new Set(categories.map(c => c.id)));
  const [editingCatId, setEditingCatId] = useState(null);
  const [editingCatName, setEditingCatName] = useState('');
  const [editingSubId, setEditingSubId] = useState(null);
  const [editingSubName, setEditingSubName] = useState('');
  const [newCatName, setNewCatName] = useState('');
  const [addingSubToCat, setAddingSubToCat] = useState(null);
  const [newSubName, setNewSubName] = useState('');
  const [deletingCat, setDeletingCat] = useState(null);
  const [deletingSub, setDeletingSub] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const toggleCat = id => setExpandedCats(prev => {
    const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next;
  });

  const doSave = async (fn) => {
    setSaving(true); setError('');
    try { await fn(); onSaved(); } catch (e) {
      setError(e.response?.data?.error || 'Error al guardar');
    } finally { setSaving(false); }
  };

  const handleSaveCat = (cat) => doSave(async () => {
    if (!editingCatName.trim()) return;
    await api.put(`/inventory/categories/${cat.id}`, { name: editingCatName.trim(), sort_order: cat.sort_order });
    setEditingCatId(null);
  });

  const handleSaveSub = (catId, sub) => doSave(async () => {
    if (!editingSubName.trim()) return;
    await api.put(`/inventory/categories/${catId}/subcategories/${sub.id}`, { name: editingSubName.trim(), sort_order: sub.sort_order });
    setEditingSubId(null);
  });

  const handleAddCat = () => doSave(async () => {
    if (!newCatName.trim()) return;
    await api.post('/inventory/categories', { name: newCatName.trim(), sort_order: categories.length });
    setNewCatName('');
  });

  const handleAddSub = (catId) => doSave(async () => {
    if (!newSubName.trim()) return;
    const cat = categories.find(c => c.id === catId);
    await api.post(`/inventory/categories/${catId}/subcategories`, { name: newSubName.trim(), sort_order: cat?.subcategories?.length || 0 });
    setNewSubName(''); setAddingSubToCat(null);
  });

  const handleDeleteCat = (cat) => doSave(async () => {
    await api.delete(`/inventory/categories/${cat.id}`);
    setDeletingCat(null);
  });

  const handleDeleteSub = (catId, sub) => doSave(async () => {
    await api.delete(`/inventory/categories/${catId}/subcategories/${sub.id}`);
    setDeletingSub(null);
  });

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 520, maxHeight: '80vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <div className="modal-title">Gestionar categorías</div>
        {error && <div className="inv-error" style={{ marginBottom: 12 }}>{error}</div>}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 20 }}>
          {categories.map(cat => (
            <div key={cat.id}>
              <div
                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', cursor: 'pointer' }}
                onClick={() => toggleCat(cat.id)}
              >
                <IconChevron open={expandedCats.has(cat.id)} />
                {editingCatId === cat.id ? (
                  <div style={{ display: 'flex', flex: 1, gap: 6 }} onClick={e => e.stopPropagation()}>
                    <input
                      className="input"
                      style={{ flex: 1, padding: '2px 8px', height: 30, fontSize: 13 }}
                      value={editingCatName}
                      onChange={e => setEditingCatName(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') handleSaveCat(cat); if (e.key === 'Escape') setEditingCatId(null); }}
                      autoFocus
                    />
                    <button className="btn btn-icon btn-ghost" onClick={() => handleSaveCat(cat)} disabled={saving}><IconCheck /></button>
                    <button className="btn btn-icon btn-ghost" onClick={() => setEditingCatId(null)}><IconX /></button>
                  </div>
                ) : (
                  <>
                    <span style={{ flex: 1, fontWeight: 600, fontSize: 14 }}>{cat.name}</span>
                    <span className="text-dim text-xs" style={{ flexShrink: 0 }}>{cat.subcategories?.length || 0} subs</span>
                    <div style={{ display: 'flex', gap: 4 }} onClick={e => e.stopPropagation()}>
                      <button className="btn btn-icon btn-ghost" title="Editar" onClick={() => { setEditingCatId(cat.id); setEditingCatName(cat.name); }}><IconPencil /></button>
                      <button className="btn btn-icon btn-ghost" style={{ color: 'var(--red)' }} title="Eliminar" onClick={() => setDeletingCat(cat)}><IconTrash /></button>
                    </div>
                  </>
                )}
              </div>

              {expandedCats.has(cat.id) && (
                <div style={{ paddingLeft: 22, display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 6 }}>
                  {cat.subcategories?.map(sub => (
                    <div key={sub.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 0' }}>
                      {editingSubId === sub.id ? (
                        <div style={{ display: 'flex', flex: 1, gap: 6 }}>
                          <input
                            className="input"
                            style={{ flex: 1, padding: '2px 8px', height: 28, fontSize: 13 }}
                            value={editingSubName}
                            onChange={e => setEditingSubName(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') handleSaveSub(cat.id, sub); if (e.key === 'Escape') setEditingSubId(null); }}
                            autoFocus
                          />
                          <button className="btn btn-icon btn-ghost" onClick={() => handleSaveSub(cat.id, sub)} disabled={saving}><IconCheck /></button>
                          <button className="btn btn-icon btn-ghost" onClick={() => setEditingSubId(null)}><IconX /></button>
                        </div>
                      ) : (
                        <>
                          <span style={{ flex: 1, fontSize: 13 }}>{sub.name}</span>
                          <button className="btn btn-icon btn-ghost" title="Editar" onClick={() => { setEditingSubId(sub.id); setEditingSubName(sub.name); }}><IconPencil /></button>
                          <button className="btn btn-icon btn-ghost" style={{ color: 'var(--red)' }} title="Eliminar" onClick={() => setDeletingSub({ catId: cat.id, sub })}><IconTrash /></button>
                        </>
                      )}
                    </div>
                  ))}

                  {addingSubToCat === cat.id ? (
                    <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                      <input
                        className="input"
                        style={{ flex: 1, padding: '2px 8px', height: 28, fontSize: 13 }}
                        placeholder="Nombre de subcategoría..."
                        value={newSubName}
                        onChange={e => setNewSubName(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') handleAddSub(cat.id);
                          if (e.key === 'Escape') { setAddingSubToCat(null); setNewSubName(''); }
                        }}
                        autoFocus
                      />
                      <button className="btn btn-icon btn-ghost" onClick={() => handleAddSub(cat.id)} disabled={saving || !newSubName.trim()}><IconCheck /></button>
                      <button className="btn btn-icon btn-ghost" onClick={() => { setAddingSubToCat(null); setNewSubName(''); }}><IconX /></button>
                    </div>
                  ) : (
                    <button
                      className="btn btn-ghost btn-sm"
                      style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 4, width: 'fit-content' }}
                      onClick={() => { setAddingSubToCat(cat.id); setNewSubName(''); }}
                    >
                      <IconPlus /> Nueva subcategoría
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="divider" style={{ marginBottom: 16 }} />
        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>Nueva categoría</div>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            className="input"
            style={{ flex: 1 }}
            placeholder="Nombre de categoría..."
            value={newCatName}
            onChange={e => setNewCatName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleAddCat(); }}
          />
          <button className="btn btn-primary btn-sm" onClick={handleAddCat} disabled={saving || !newCatName.trim()}>
            <IconPlus /> Agregar
          </button>
        </div>

        {deletingCat && (
          <div style={{ background: 'var(--surface-raised)', border: '1px solid var(--red)', borderRadius: 8, padding: 16, marginTop: 16 }}>
            <p style={{ fontSize: 14, marginBottom: 12 }}>
              ¿Eliminar <strong>{deletingCat.name}</strong>? Los ítems y packs pasarán a "Genéricos".
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost btn-sm" onClick={() => setDeletingCat(null)}>Cancelar</button>
              <button className="btn btn-danger btn-sm" onClick={() => handleDeleteCat(deletingCat)} disabled={saving}>Eliminar</button>
            </div>
          </div>
        )}

        {deletingSub && (
          <div style={{ background: 'var(--surface-raised)', border: '1px solid var(--red)', borderRadius: 8, padding: 16, marginTop: 16 }}>
            <p style={{ fontSize: 14, marginBottom: 12 }}>
              ¿Eliminar <strong>{deletingSub.sub.name}</strong>? Los ítems quedarán sin subcategoría.
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost btn-sm" onClick={() => setDeletingSub(null)}>Cancelar</button>
              <button className="btn btn-danger btn-sm" onClick={() => handleDeleteSub(deletingSub.catId, deletingSub.sub)} disabled={saving}>Eliminar</button>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
          <button className="btn btn-ghost" onClick={onClose}>Cerrar</button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function InventoryPage() {
  useScrollRestoration('inventory');
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const canReport = user?.role === 'admin' || user?.role === 'deposito';

  const [items, setItems] = useState([]);
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);

  // undefined = cerrado, null = nuevo, objeto = editar
  const [incidentTarget, setIncidentTarget] = useState(null);
  const [itemFormTarget, setItemFormTarget] = useState(undefined);
  const [packFormTarget, setPackFormTarget] = useState(undefined);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [viewItem, setViewItem] = useState(null);
  const [categories, setCategories] = useState([]);
  const [groupEditTarget, setGroupEditTarget] = useState(undefined);
  const [showCategoriesModal, setShowCategoriesModal] = useState(false);

  const loadCategories = useCallback(async () => {
    try {
      const res = await api.get('/inventory/categories');
      setCategories(res.data);
    } catch (e) { console.error(e); }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [itemsRes, packsRes, catsRes] = await Promise.all([
        api.get('/inventory/items'),
        api.get('/inventory/packs'),
        api.get('/inventory/categories'),
      ]);
      setItems(itemsRes.data);
      setPacks(packsRes.data);
      setCategories(catsRes.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      if (deleteTarget.type === 'item') {
        await api.delete(`/inventory/items/${deleteTarget.id}`);
      } else {
        await api.delete(`/inventory/packs/${deleteTarget.id}`);
      }
      setDeleteTarget(null);
      load();
    } catch (e) {
      alert(e.response?.data?.error || 'Error al eliminar');
    }
  };

  return (
    <div className="inventory-page">
      <div className="page-header" style={{ marginBottom: 20 }}>
        <div>
          <h1 className="page-title">Inventario</h1>
          <p className="page-sub">{items.length} ítems · {packs.length} packs</p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center" style={{ gap: 12, padding: '40px 0', color: 'var(--text-dim)' }}>
          <div className="spinner" style={{ width: 20, height: 20 }} />
          Cargando inventario...
        </div>
      ) : (
        <ConsultaTab
          items={items}
          packs={packs}
          canReport={canReport}
          isAdmin={isAdmin}
          categories={categories}
          onReportItem={item => setIncidentTarget({ type: 'item', id: item.id, name: item.name })}
          onReportPack={pack => setIncidentTarget({ type: 'pack', id: pack.id, name: pack.name })}
          onViewItem={item => setViewItem(item)}
          onUpdateItem={updated => setItems(prev => prev.map(i => i.id === updated.id ? updated : i))}
          onAdd={() => setItemFormTarget(null)}
          onDeleteItem={item => setDeleteTarget({ type: 'item', id: item.id, name: item.name })}
          onEditPack={pack => setPackFormTarget(pack)}
          onDeletePack={pack => setDeleteTarget({ type: 'pack', id: pack.id, name: pack.name })}
          onEditGroup={units => setGroupEditTarget(units)}
          onManageCategories={() => setShowCategoriesModal(true)}
        />
      )}

      {/* ── Modals ── */}
      {incidentTarget && (
        <IncidentModal
          target={incidentTarget}
          onClose={() => setIncidentTarget(null)}
          onSaved={load}
        />
      )}

      {itemFormTarget !== undefined && (
        <ItemFormModal
          item={itemFormTarget}
          categories={categories}
          onClose={saved => { setItemFormTarget(undefined); if (saved) load(); }}
        />
      )}

      {groupEditTarget !== undefined && (
        <ItemFormModal
          item={null}
          categories={categories}
          groupUnits={groupEditTarget}
          onClose={saved => { setGroupEditTarget(undefined); if (saved) load(); }}
        />
      )}

      {packFormTarget !== undefined && (
        <PackModal
          pack={packFormTarget}
          allItems={items}
          onClose={() => setPackFormTarget(undefined)}
          onSaved={load}
        />
      )}

      {deleteTarget && (
        <DeleteModal
          name={deleteTarget.name}
          onClose={() => setDeleteTarget(null)}
          onConfirm={handleDeleteConfirm}
        />
      )}

      {showCategoriesModal && (
        <CategoriesModal
          categories={categories}
          onClose={() => setShowCategoriesModal(false)}
          onSaved={loadCategories}
        />
      )}

      {viewItem && (
        <ItemDetailModal
          item={viewItem}
          onClose={shouldRefresh => { setViewItem(null); if (shouldRefresh) load(); }}
          onEdit={isAdmin ? item => { setViewItem(null); setItemFormTarget(item); } : null}
        />
      )}
    </div>
  );
}
